import { Button, Input, Loader, Text } from '@southfields-digital/mpxlive-components';
import { useEffect, useMemo, useState, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { validate as isValidUUID } from 'uuid';

import { getRepeaterItems, transformSportDataValue } from 'src/components/GraphicPropertiesForm';
import Repeater from 'src/components/Repeater';
import { Option, Value } from 'src/components/Select';
import { LegacyGraphicRef } from 'src/config/graphicRefs';
import { getGraphicRef } from 'src/utils';
import { StateType } from 'src/redux/reducers';
import { REQUEST_GET_SPORT_DATA_STANDINGS } from 'src/redux/reducers/sportData';

import SportDataSelect from '../SportDataSelect';
import { getDomesticCompetitions, getPropertyValues } from '../utils';

import styles from './Standings.module.scss';

type Props = {
  handleImportData: (values: PropertyValue[]) => void;
  selectedGraphic: PlaylistGraphic;
  selectedProperty: RepeaterProperties;
};

const LanguageOptions: Option[] = [
  { value: 'en-op', label: 'English' },
  { value: 'nl', label: 'Dutch' },
];

const TypeOptions: Option[] = [
  { value: 'no', label: 'Normal' },
  { value: 'yes', label: 'Virtual' },
];

export default function Standings({ handleImportData, selectedGraphic, selectedProperty }: Props) {
  const dispatch = useDispatch();

  const { standings, tournamentCalendar } = useSelector((state: StateType) => state.sportData);
  const { data: translations } = useSelector((state: StateType) => state.dataProviderTranslation);

  const { matchesRef } = getGraphicRef(selectedGraphic.graphic.ref);

  const [tmcl, setTmcl] = useState<string>(standings.data.tournamentCalendarId || '');
  const [language, setLanguage] = useState<Value>(LanguageOptions[0].value);
  const [type, setType] = useState<Value>(TypeOptions[0].value);

  const [startRange, setStartRange] = useState<number>(1);
  const [endRange, setEndRange] = useState<number>(0);

  const competitions = useMemo(
    () => getDomesticCompetitions(tournamentCalendar.data?.competition),
    [tournamentCalendar.data?.competition]
  );

  const propertyValues = useMemo(
    () =>
      getPropertyValues(
        selectedGraphic,
        standings.data.standingsData?.stage
          .find((stage) => stage.name === 'Regular Season' || stage.name === 'League Stage')
          ?.division.find((division) => division.type === 'total')?.ranking ?? [],
        translations ?? []
      ),
    [selectedGraphic, standings.data.standingsData, translations]
  );

  const transformedPropertyValues = useMemo(() => {
    if (!propertyValues.length) return propertyValues;
    return isValidUUID(selectedProperty.id)
      ? (transformSportDataValue(propertyValues, selectedProperty) as PropertyValue[])
      : propertyValues;
  }, [propertyValues, selectedProperty]);

  const repeaterValues = useMemo(
    () =>
      transformedPropertyValues.length
        ? getRepeaterItems(selectedProperty, transformedPropertyValues)
        : [],
    [transformedPropertyValues, selectedProperty]
  );

  const errorInRange = useMemo(() => startRange > endRange, [startRange, endRange]);

  useEffect(() => {
    if (!tmcl) return;
    setEndRange(0);
    dispatch({
      type: REQUEST_GET_SPORT_DATA_STANDINGS,
      payload: { tmcl, _lcl: language, live: type },
    });
  }, [tmcl, type, language]);

  useEffect(() => {
    if (!transformedPropertyValues.length) return;

    const totalValues = transformedPropertyValues.length;

    const setRange = (start: number, end: number) => {
      setStartRange(start);
      setEndRange(end);
    };

    if (matchesRef(LegacyGraphicRef.LeagueTable) && totalValues === 36) {
      const leagueTableRanges: Record<string, [number, number]> = {
        column1Rows: [1, 8],
        column2Rows: [9, 16],
        column3Rows: [17, 24],
        column4Rows: [25, 36],
      };

      const range = leagueTableRanges[selectedProperty.id];
      if (range) {
        setRange(range[0], range[1]);
        return;
      }
    }

    // Use name if id is a UUID (generated id), else use id since its the custom legacy way
    const propertyId = isValidUUID(selectedProperty.id)
      ? selectedProperty.name.toLowerCase()
      : selectedProperty.id.toLowerCase();
    const midRange = Math.floor(totalValues / 2);

    if (propertyId.includes('left')) {
      setRange(1, midRange);
    } else if (propertyId.includes('right')) {
      setRange(midRange + 1, totalValues);
    } else if (!endRange) {
      setRange(1, totalValues);
    }
  }, [transformedPropertyValues, selectedProperty, matchesRef, endRange]);

  const handleClickImport = () => {
    if (errorInRange) return;
    const newValues = transformedPropertyValues.slice(startRange - 1, endRange);
    handleImportData(newValues);
  };

  if (tournamentCalendar.loading) return <Loader />;
  if (standings.loading || !standings.data.standingsData?.stage.length) {
    return (
      <div className={styles.Container}>
        <div className={styles.Header} />
        <div className={styles.Content}>
          <div className={styles.FilterContainer}>
            <div className={styles.League}>
              <SportDataSelect
                label="Selected league"
                value={tmcl}
                options={competitions}
                onChange={(e) => setTmcl(e.currentTarget.value)}
              />
            </div>
            <div className={styles.Type}>
              <SportDataSelect
                label="Type"
                value={type}
                options={TypeOptions}
                onChange={(e) => setType(e.currentTarget.value)}
              />
            </div>
            <div className={styles.Language}>
              <SportDataSelect
                label="Language"
                value={language}
                options={LanguageOptions}
                onChange={(e) => setLanguage(e.currentTarget.value)}
              />
            </div>
          </div>
          {standings.loading && (
            <div className={styles.LoadingContainer}>
              <Loader className={styles.Loading} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.Container}>
      <div className={styles.Header}>
        {transformedPropertyValues.length > 0 && (
          <div className={styles.RangeContainer}>
            <Input
              type="number"
              min={1}
              max={transformedPropertyValues.length}
              className={styles.RangeInput}
              value={startRange}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setStartRange(+e.target.value)}
            />
            <Text as="span" className={styles.RangeLabel}>
              t/m
            </Text>
            <Input
              type="number"
              min={startRange || 1}
              max={transformedPropertyValues.length}
              className={styles.RangeInput}
              value={endRange}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setEndRange(+e.target.value)}
            />
          </div>
        )}
        <Button className={styles.ImportButton} onClick={handleClickImport} disabled={errorInRange}>
          Import
        </Button>
      </div>
      <div className={styles.Content}>
        <div className={styles.FilterContainer}>
          <div className={styles.League}>
            <SportDataSelect
              label="Selected league"
              value={tmcl}
              options={competitions}
              onChange={(e) => setTmcl(e.currentTarget.value)}
            />
          </div>
          <div className={styles.Type}>
            <SportDataSelect
              label="Type"
              value={type}
              options={TypeOptions}
              onChange={(e) => setType(e.currentTarget.value)}
            />
          </div>
          <div className={styles.Language}>
            <SportDataSelect
              label="Language"
              value={language}
              options={LanguageOptions}
              onChange={(e) => setLanguage(e.currentTarget.value)}
            />
          </div>
        </div>
        <Repeater
          id=""
          onChangeRows={() => {}}
          items={repeaterValues}
          isDisabled
          excludedIds={['divider', 'highlight']}
        />
      </div>
    </div>
  );
}
